import { FC } from 'react'

import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import { Box, Theme, alpha, useTheme } from '@mui/material'
import { SystemStyleObject } from '@mui/system'

import { clampPct } from '../../helpers/showcase'

/** Motivation intensity is on a 0–5 scale; used to size the bar. */
const MOTIVATION_SCORE_MAX = 5

/** Height of the coverage track. The target marker overhangs it slightly. */
const BAR_HEIGHT = 12

/** Class hook so a parent's `:hover` can fan the two icon markers apart. */
const GAP_BAR_MARKER_CLASS = 'gap-bar-marker'

/**
 * Hover/focus rules a clickable comparison card spreads onto its own `sx` so the
 * two game-icon markers fan apart ("pop to side") when hovered — the same
 * affordance the old icon dumbbell used, now on the gap bar. Each marker fans
 * toward its own visual side so a near-equal (overlapping) pair becomes legible.
 * Single-icon (single-game) bars don't get the modifier classes, so their
 * marker keeps its own pop-up hover untouched.
 */
export const gapBarIconsFanApartSx: SystemStyleObject<Theme> = {
  [`& .${GAP_BAR_MARKER_CLASS}--left`]: { transform: 'translate(calc(-50% - 16px), -50%)' },
  [`& .${GAP_BAR_MARKER_CLASS}--right`]: { transform: 'translate(calc(-50% + 16px), -50%)' },
}

/**
 * Keyframes a card hosting a gap bar declares at its own `sx` root. The
 * translation is exactly one stripe period (8px on a 135° hatch ⇒ 11.3137px
 * horizontally) so the GPU-transformed loop is perfectly seamless.
 */
export const gapBarFlowKeyframesSx: SystemStyleObject<Theme> = {
  '@keyframes gapBarFlow': {
    to: { transform: 'translateX(11.3137px)' },
  },
}

/**
 * Hover-scoped rule that runs the hatched gap band's continuous "flow". Spread
 * inside a card's `&:hover` block; pair with {@link gapBarFlowKeyframesSx} at
 * the `sx` root. Held back under `prefers-reduced-motion` since it is the only
 * looping motion on the card.
 */
export const gapBarStripesFlowSx: SystemStyleObject<Theme> = {
  '& .gap-bar-stripes': {
    '@media (prefers-reduced-motion: no-preference)': { animation: 'gapBarFlow 0.7s linear infinite' },
  },
}

/**
 * The "exploded engine" reveal shared by both diagnosis cards: the bar swells,
 * the gap stripes flow and the marker halos bloom. Spread this inside a card's
 * `'&:hover, &.Mui-focusVisible'` block, then layer on whatever choreography is
 * specific to that card (e.g. a lone marker popping out vs two fanning apart).
 *
 * Kept here rather than duplicated per card because the single-game and
 * comparison cards are deliberate visual mirrors — a tweak to one has to land
 * on the other or the two views drift apart.
 */
export const gapBarRevealOnHoverSx: SystemStyleObject<Theme> = {
  '& .gap-bar-track': { transform: 'scaleY(1.32)' },
  '& .gap-bar-fill': { filter: 'saturate(1.15) brightness(1.05)' },
  '& .gap-bar-halo': { opacity: 1, transform: 'translateX(-50%) scale(1.9)' },
  ...gapBarStripesFlowSx,
  '& .gap-bar-scores': { transform: 'scale(1.12)' },
}

interface MotivationGapBarProps {
  /** In-game (Game & Live Events) intensity 0–5 — the target the creatives should echo. */
  target: number
  /** Creative Ads intensity 0–5 — the coverage the creatives actually deliver. */
  delivered: number
  /**
   * Blind spots render the shortfall (target − delivered) as a hatched risk
   * band; aligned cards render the over-coverage as a calm success band.
   */
  diverging: boolean
  /** Colour of the delivered fill (Creative Ads). */
  deliveredColor: string
  /** Colour of the target marker (Game & Live Events). */
  targetColor: string
  targetLabel: string
  deliveredLabel: string
  /**
   * Optional icon (e.g. the game's app icon) shown at the target goalpost in
   * place of the plain dot, so the marker reads as "this game's in-game
   * emphasis" at a glance. Falls back to the dot when omitted.
   */
  targetIcon?: string
  /**
   * Comparison mode: render a second marker (capping the delivered fill) so the
   * bar shows *both* entities as game-icon chips instead of a single goalpost.
   * The two markers fan apart on the parent card's hover (see
   * {@link gapBarIconsFanApartSx}). When false the bar keeps its single-game
   * goalpost treatment.
   */
  showDeliveredMarker?: boolean
  /** Icon for the delivered-side marker (only used when `showDeliveredMarker`). */
  deliveredIcon?: string
  /**
   * When the delivered side has no app icon (e.g. a genre), draw the generic
   * category badge at its marker instead of a plain dot, so the compared
   * game/genre still reads as an entity chip. Only used in comparison mode.
   */
  deliveredIsGenre?: boolean
}

/**
 * Bullet-style coverage bar for one motivation's two signals on a shared 0–5
 * track. Unlike a symmetric dumbbell, it tells the blind-spot story
 * directionally:
 *
 *  - a solid fill from 0 → `delivered` (what the Creative Ads actually cover),
 *  - a hatched band from `delivered` → `target` — the uncovered shortfall the
 *    creatives need to close (the blind spot made literal),
 *  - a `target` marker (dark goalpost + dot) at the in-game emphasis level the
 *    creatives should reach.
 *
 * The same colours map back to the section legend (delivered = Creative Ads,
 * marker = Game & Live Events), and the hatched band echoes the card's red gap
 * chip — so the gap is always visible, never hidden behind a hover.
 */
export const MotivationGapBar: FC<MotivationGapBarProps> = ({
  target,
  delivered,
  diverging,
  deliveredColor,
  targetColor,
  targetLabel,
  deliveredLabel,
  targetIcon,
  showDeliveredMarker = false,
  deliveredIcon,
  deliveredIsGenre = false,
}) => {
  const theme = useTheme()
  const targetPct = clampPct((target / MOTIVATION_SCORE_MAX) * 100)
  const deliveredPct = clampPct((delivered / MOTIVATION_SCORE_MAX) * 100)
  const gapStart = Math.min(deliveredPct, targetPct)
  const gapWidth = Math.abs(targetPct - deliveredPct)

  // In comparison mode the two markers fan toward their own visual side on
  // hover, keyed on position (not side) since either entity can hold the
  // higher score. The marker further left fans left; the one further right
  // fans right. Ties default to target-left so the layout stays deterministic.
  const targetIsLeft = targetPct <= deliveredPct
  const targetFan: 'left' | 'right' | undefined = showDeliveredMarker ? (targetIsLeft ? 'left' : 'right') : undefined
  const deliveredFan: 'left' | 'right' = targetIsLeft ? 'right' : 'left'

  // The shortfall band is a diagonal hatch in the category colour so it reads
  // as "uncovered" rather than a solid fill; aligned over-coverage is a calm
  // solid tint instead.
  const bandBaseColor = diverging ? theme.palette.error.main : theme.palette.success.main
  const bandHatch = `repeating-linear-gradient(135deg, ${alpha(bandBaseColor, 0.5)} 0, ${alpha(bandBaseColor, 0.5)} 4px, ${alpha(
    bandBaseColor,
    0.16
  )} 4px, ${alpha(bandBaseColor, 0.16)} 8px)`

  return (
    <Box
      role="img"
      aria-label={`${deliveredLabel} ${delivered.toFixed(1)}, ${targetLabel} ${target.toFixed(1)}`}
      sx={{
        position: 'relative',
        isolation: 'isolate',
        flex: 1,
        minWidth: 80,
        height: BAR_HEIGHT + 12,
        display: 'flex',
        alignItems: 'center',
        mx: '5px',
      }}
    >
      {/* 0–5 coverage track with the delivered fill + shortfall band clipped
          inside. `transformOrigin: center` lets the parent card's hover swell it
          (scaleY) in place without shifting its resting position. */}
      <Box
        className="gap-bar-track"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: BAR_HEIGHT,
          borderRadius: '999px',
          bgcolor: 'action.hover',
          overflow: 'hidden',
          transformOrigin: 'center',
          transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Delivered coverage (Creative Ads). */}
        <Box
          className="gap-bar-fill"
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${deliveredPct}%`,
            bgcolor: deliveredColor,
            // Left-rounded only so the fill meets the hatched gap band flush.
            borderRadius: '999px 0 0 999px',
            transition: 'width 0.3s ease, filter 0.25s ease',
          }}
        />
        {/* Shortfall / over-coverage band between the two signals. The diverging
            hatch lives on an oversized inner layer so the parent card's hover can
            scroll it via a GPU `translateX` (seamless, jitter-free) instead of
            animating `background-position`. */}
        {gapWidth > 0 && (
          <Box
            className="gap-bar-band"
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${gapStart}%`,
              width: `${gapWidth}%`,
              overflow: 'hidden',
              backgroundColor: diverging ? undefined : alpha(bandBaseColor, 0.18),
              transition: 'left 0.3s ease, width 0.3s ease',
            }}
          >
            {diverging && (
              <Box
                className="gap-bar-stripes"
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  // Overhang one stripe period on the left so the scroll never
                  // exposes an uncovered edge as it drifts.
                  left: '-12px',
                  width: 'calc(100% + 12px)',
                  backgroundImage: bandHatch,
                  willChange: 'transform',
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Hairline gridlines at each whole intensity step for a sense of scale. */}
      {[1, 2, 3, 4].map((step) => (
        <Box
          key={step}
          aria-hidden
          sx={{
            position: 'absolute',
            left: `${(step / MOTIVATION_SCORE_MAX) * 100}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            height: BAR_HEIGHT,
            width: '1px',
            bgcolor: 'background.paper',
            opacity: 0.5,
            zIndex: 1,
          }}
        />
      ))}

      {/* Delivered-side marker — only in comparison mode, capping the fill so the
          bar shows the second entity as an icon chip too. Rendered before the
          target marker so the target stacks on top when the two crowd together. */}
      {showDeliveredMarker && (
        <GapBarMarker
          pct={deliveredPct}
          color={deliveredColor}
          icon={deliveredIcon}
          genericFallback={deliveredIsGenre}
          showGoalpost={false}
          fan={deliveredFan}
          onTop={!targetIsLeft}
        />
      )}

      {/* Target marker — a dark goalpost the creatives should reach in single-game
          mode; one of two fanning icon chips in comparison mode. */}
      <GapBarMarker pct={targetPct} color={targetColor} icon={targetIcon} showGoalpost={!showDeliveredMarker} fan={targetFan} onTop={targetIsLeft} />
    </Box>
  )
}

interface GapBarMarkerProps {
  /** 0–100 position along the track. */
  pct: number
  /** Ring / goalpost / dot colour for this side. */
  color: string
  /** App icon URL; falls back to a coloured dot when omitted (e.g. genres). */
  icon?: string
  /**
   * When there is no `icon`, draw the generic category badge (a genre's
   * stand-in) instead of the plain dot.
   */
  genericFallback?: boolean
  /** Single-game mode draws the vertical goalpost line behind the glyph. */
  showGoalpost: boolean
  /**
   * Fan direction on the parent card's hover (comparison mode). Omitted leaves
   * the marker static so the single-game glyph keeps its own pop-up hover.
   */
  fan?: 'left' | 'right'
  /** Stacks this marker above its sibling when the two overlap at rest. */
  onTop: boolean
}

/**
 * One marker on the gap-bar track: a halo that blooms on hover, plus either the
 * entity's app-icon chip (ringed in the side colour) or a fallback dot. In
 * comparison mode the whole marker carries a `--left` / `--right` modifier class
 * so the parent card can fan it toward its own side (see
 * {@link gapBarIconsFanApartSx}).
 */
const GapBarMarker: FC<GapBarMarkerProps> = ({ pct, color, icon, genericFallback = false, showGoalpost, fan, onTop }) => (
  <Box
    aria-hidden
    className={fan ? `${GAP_BAR_MARKER_CLASS} ${GAP_BAR_MARKER_CLASS}--${fan}` : GAP_BAR_MARKER_CLASS}
    sx={{
      position: 'absolute',
      left: `${pct}%`,
      top: '50%',
      transform: 'translate(-50%, -50%)',
      height: BAR_HEIGHT + 10,
      width: 10,
      zIndex: onTop ? 3 : 2,
      pointerEvents: 'none',
      transition: 'left 0.3s ease, transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
    }}
  >
    {showGoalpost && (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '2.5px',
          bgcolor: color,
          borderRadius: '999px',
        }}
      />
    )}
    {/* Soft halo that blooms behind the glyph on hover (the "pop"). */}
    <Box
      className="gap-bar-halo"
      sx={{
        position: 'absolute',
        top: -7,
        left: '50%',
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: alpha(color, 0.45),
        filter: 'blur(3px)',
        opacity: 0,
        zIndex: 1,
        transformOrigin: 'center',
        transform: 'translateX(-50%) scale(0.4)',
        transition: 'opacity 0.25s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    />
    {icon ? (
      // The entity's app icon caps the marker (a rounded-square chip ringed in
      // the side colour) so it reads as "this game" at a glance.
      <Box
        component="img"
        src={icon}
        alt=""
        className="gap-bar-glyph"
        sx={{
          position: 'absolute',
          top: -7,
          left: '50%',
          width: 22,
          height: 22,
          borderRadius: '6px',
          objectFit: 'cover',
          bgcolor: 'background.paper',
          border: '2px solid',
          borderColor: color,
          boxShadow: 1,
          zIndex: 2,
          transformOrigin: 'center bottom',
          transform: 'translateX(-50%)',
          transition: 'transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease',
        }}
      />
    ) : genericFallback ? (
      // No app icon (e.g. a genre): the generic category badge stands in for
      // the entity, sized + ringed like the icon chip so the two sides match.
      <Box
        className="gap-bar-glyph"
        sx={{
          position: 'absolute',
          top: -7,
          left: '50%',
          width: 22,
          height: 22,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          bgcolor: 'background.paper',
          border: '2px solid',
          borderColor: color,
          boxShadow: 1,
          zIndex: 2,
          transformOrigin: 'center bottom',
          transform: 'translateX(-50%)',
          transition: 'transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease',
        }}
      >
        <CategoryRoundedIcon sx={{ fontSize: 13 }} />
      </Box>
    ) : (
      <Box
        className="gap-bar-glyph"
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          width: 9,
          height: 9,
          borderRadius: '50%',
          bgcolor: color,
          border: '2px solid',
          borderColor: 'background.paper',
          boxShadow: 1,
          zIndex: 2,
          transformOrigin: 'center bottom',
          transform: 'translateX(-50%)',
          transition: 'transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease',
        }}
      />
    )}
  </Box>
)
